import { useMutation } from "@apollo/client";
import { SnackbarContent } from "components/global/snackbar/SnackbarContent";
import { useSnackbarContext } from "contexts/SnackbarContext";
import { useFridgeInformation } from "hooks/useFridgeInformation";
import { useAppDispatch } from "hooks/utilsHooks/storeHooks";
import type React from "react";
import { type ReactElement, useState } from "react";
import { setIsReportingIssue } from "slices/appStateSlice";
import { OrderFeedbackRatingType, type OrderFeedbackReasonType } from "types";
import { SEND_FEEDBACK_MUTATION } from "utils/gqlOperations";
import { BasicModal } from "./BasicModal";
import { ReportIssueModalContent } from "./modalsContents/ReportIssueModalContent";

type ReportIssueModalProps = {
	toggleButton: ReactElement<{ onClick?: (e: React.MouseEvent<HTMLElement>) => void }>;
	transactionId: string;
	resetOrder: () => void;
	onClick?: () => void;
};

export const ReportIssueModal = ({ toggleButton, transactionId, resetOrder, onClick }: ReportIssueModalProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const { id } = useFridgeInformation();
	const dispatch = useAppDispatch();

	const { openSnackbarWithContent } = useSnackbarContext();
	const [sendNegativeFeedbackMutation] = useMutation(SEND_FEEDBACK_MUTATION);

	// We do not really care when the feedback is sent or finished sending,
	// we just go on so next customer can buy food
	// biome-ignore lint/suspicious/useAwait: must return Promise<void> to match ReportIssueModalContentProps
	const sendFeedback = async (reason: OrderFeedbackReasonType | null) => {
		// Re-enable the suspended autolock + 10s timers before triggering resetOrder so the
		// next order isn't stuck with stale "is reporting" state. Every close path (user
		// picks a reason, presses Cancel, modal countdown expires, backdrop/Esc) funnels
		// through here, so this single dispatch covers them all.
		dispatch(setIsReportingIssue(false));
		openSnackbarWithContent(<SnackbarContent variant="feedbackNegative" />);
		if (id && transactionId) {
			sendNegativeFeedbackMutation({
				variables: {
					input: {
						orderId: transactionId,
						type: OrderFeedbackRatingType.NEGATIVE,
						reason: reason,
					},
				},
			});
		}
		resetOrder();
	};

	const handleCloseModal = () => {
		setIsOpen(false);
		sendFeedback(null);
	};

	const handleClick = () => {
		if (onClick) {
			onClick();
		}
	};

	return (
		<BasicModal
			onClose={() => handleCloseModal()}
			onClick={() => handleClick()}
			toggleButton={toggleButton}
			fullHeight={true}
			closable={true}
			modalContent={
				<ReportIssueModalContent
					onClose={() => handleCloseModal()}
					onSendFeedbackAction={(reason) => sendFeedback(reason)}
				/>
			}
			shouldClose={isOpen}
		/>
	);
};
